import { SideMenuGroup } from '../SideMenu';

export type AccountMenuOptions = {
  /** What this brand calls the auto-renewal service — 'DiscountLock', 'Renew & Save'. */
  autoRenewServiceName: string;
  /** Where that brand's auto-renewal page lives; each spells the path its own way. */
  autoRenewUrl: string;
  /**
   * Whether this brand has a digital library at all. Only DiscountMags does: TopMags,
   * MagSpot and MagZoom sell no digital editions — `skip_digital` is set on all three in
   * sites config — and `domain-overrides` blanks the library page itself on the two of
   * them that ever linked to it.
   *
   * The brand's own Account layout answers this, not sites config: it is settled by
   * which brand you are on rather than which storefront (every DiscountMags locale has
   * one, no TopMags storefront does), and it is the same kind of fact as the six
   * hardcoded entries around it. If a single storefront ever needs to disagree with its
   * brand, it becomes a site option and arrives here through the props builder the way
   * the auto-renewal name already does.
   */
  digitalLibrary?: boolean;
};

/**
 * The signed-in customer's pages.
 *
 * Hardcoded like the institutional menu's, and for the same reason — these are a fixed
 * handful of pages, editable nowhere — apart from what the brands genuinely disagree
 * about. The auto-renewal service is the brand's own in both directions: it is *called*
 * something different everywhere (DiscountLock here, PriceShield on MagSpot, Renew &
 * Save on TopMags) and it *lives* somewhere different too, because each brand spells its
 * path with its own word for the service. Both halves of that entry are passed in rather
 * than written down; both come off sites config, through AccountLayoutPropsBuilder.
 *
 * Most of the rest are still the legacy `customer.php` pages, which is why their hrefs
 * are query strings; they come back one at a time, and each one that does is one line
 * here. Paths are relative because the AU site serves from a base directory — every one
 * of them goes through buildUrl before it is rendered.
 *
 * Shared rather than a brand's, because it is the same account on every brand: the
 * legacy customer account is one codebase, and what differs is parameterised here.
 */
export function accountMenu({
  autoRenewServiceName,
  autoRenewUrl,
  digitalLibrary = false,
}: AccountMenuOptions): SideMenuGroup[] {
  return [
    {
      title: 'Account',
      items: [
        { label: 'Dashboard', href: '/customer?xCmd=account' },
        { label: 'Profile', href: '/customer?xCmd=acshow' },
        { label: 'Orders', href: '/customer?xCmd=orders' },
        { label: autoRenewServiceName, href: autoRenewUrl },
        ...(digitalLibrary ? [{ label: 'Digital Library', href: '/customer?xCmd=digitallibrary' }] : []),
        { label: 'Addresses', href: '/customer?xCmd=adshow' },
        // Named for what it covers rather than for one channel: the page behind
        // it now carries both email and SMS, and the header account menu has
        // called it Notifications all along.
        { label: 'Notifications', href: '/customer?xCmd=epshow' },
      ],
    },
  ];
}
